"use client";
import React, { useState, useMemo } from "react";
import ProjectCard from "@/components/ProjectCard";
import projectsData from "../../data.json";
import { motion, AnimatePresence } from "framer-motion";

const allTags = Array.from(
  new Set(projectsData.flatMap((p) => p.techStack || []))
).sort();

export default function AllProjects() {
  const [filter, setFilter] = useState("All");

  const filteredProjects = useMemo(() => {
    if (filter === "All") return projectsData;
    return projectsData.filter((p) => p.techStack?.includes(filter));
  }, [filter]);

  const projects = filteredProjects.map((p) => ({
    name: p.name,
    summary: p.summary,
    techStack: p.techStack || [],
    link: p.slug ? `/projects/${p.slug}` : p.link || "#",
    thumbnail: p.thumbnail || p.images?.[0] || p.screenshot || "/image.png",
    github: p.github,
    description: p.description
  }));

  return (
    <div className="min-h-screen bg-black pt-24 pb-20 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        <header className="mb-16">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            All Works
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl">
            A comprehensive list of my projects, experiments, and open source contributions.
          </p>
        </header>

        {/* Filter Bar */}
        <div className="mb-12 overflow-x-auto pb-4 no-scrollbar">
          <div className="flex gap-2">
            <button
              onClick={() => setFilter("All")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${filter === "All"
                ? "bg-white text-black"
                : "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white"
                }`}
            >
              All
            </button>
            {allTags.map((tag) => (
              <button
                key={tag}
                onClick={() => setFilter(tag)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${filter === tag
                  ? "bg-white text-black"
                  : "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white"
                  }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          <AnimatePresence mode="popLayout">
            {projects.map((project, index) => (
              <motion.div
                key={project.name}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
              >
                <ProjectCard project={project} index={index} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {projects.length === 0 && (
          <div className="text-center py-20 text-muted-foreground">
            No projects found with this tag.
          </div>
        )}
      </div>
    </div>
  );
}
